import React, { useState, useEffect, useCallback } from 'react';
import { collection, doc, getDocs, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';

function PendingInvites({ userId, onInviteHandled }) {
    const [invites, setInvites] = useState([]);

    const fetchInvites = useCallback(async () => {
        if (!userId) return;
        try {
            const snap = await getDocs(collection(db, 'userProfiles', userId, 'invites'));
            setInvites(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.status === 'pending'));
        } catch (e) {
            console.error("Error fetching invites", e);
        }
    }, [userId]);

    useEffect(() => { fetchInvites(); }, [fetchInvites]);

    const handleAccept = async (invite) => {
        try {
            // 1. Add to Group Members
            const memberRef = doc(db, 'groups', invite.groupId, 'members', userId);
            await setDoc(memberRef, {
                userId: userId,
                userName: invite.invitedByName === userId ? 'Me' : 'Player', // Fallback
                joinedAt: Timestamp.now(),
                role: 'member',
                status: 'waitlist' // Default to waitlist, ideally check capacity
            });

            // 2. Add to User Groups
            const userGroupRef = doc(db, 'userProfiles', userId, 'groups', invite.groupId);
            await setDoc(userGroupRef, {
                groupId: invite.groupId,
                groupName: invite.groupName,
                joinedAt: Timestamp.now(),
                role: 'member'
            });

            // 3. Update Invite
            await updateDoc(doc(db, 'userProfiles', userId, 'invites', invite.groupId), { status: 'accepted' });

            toast.success("Joined group!");
            fetchInvites();
            if (onInviteHandled) onInviteHandled();
        } catch (e) {
            console.error(e);
            toast.error("Failed to join");
        }
    };

    const handleDecline = async (invite) => {
        try {
            await updateDoc(doc(db, 'userProfiles', userId, 'invites', invite.groupId), { status: 'declined' });
            toast.success("Declined");
            fetchInvites();
        } catch (e) {
            console.error(e);
            toast.error("Failed to decline");
        }
    };

    if (invites.length === 0) return null;

    return (
        <div className="mb-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="text-lg font-semibold text-yellow-800 mb-4">Pending Invitations</h3>
            <div className="space-y-3">
                {invites.map(invite => (
                    <div key={invite.id} className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
                        <div>
                            <div className="font-medium">{invite.groupName}</div>
                            <div className="text-sm text-gray-600">Invited by {invite.invitedByName}</div>
                        </div>
                        <div className="space-x-2">
                            <button
                                onClick={() => handleAccept(invite)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                Accept
                            </button>
                            <button
                                onClick={() => handleDecline(invite)}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                Decline
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default PendingInvites;
